import { ListSubheader, Menu, MenuItem, Tooltip } from '@mui/material';
import { ForcedTheme } from 'components/ForcedTheme';
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useDrawerNavState } from 'state/useDrawerNavState';
import CameraIcon from 'assets/camera.svg?react';
import { TFunction } from 'i18next';
import { getDenseResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { useHeaderActionCommandPaletteActions } from 'hooks/commandPaletteActions';
import { DrawerNav } from './DrawerNav';
import { HeaderMenuAction, HeaderMenuSection } from './types';
import { HeaderAppBar, HeaderIconAction } from './HeaderAppBar';

export type HeaderBarProps = {
  title?: string;
  action?: 'noControls' | 'back' | string;
  elevation?: number;
  forceDark?: boolean;
  iconActions?: HeaderIconAction[];
  menuSections?: HeaderMenuSection[];
};

export const getCameraActions = (
  t: TFunction,
  to: string = '/add_report',
): HeaderIconAction[] => {
  if (!Capacitor.isNativePlatform()) {
    return [];
  }
  return [
    {
      label: t('header.actions.submitPhoto'),
      to,
      Icon: <CameraIcon />,
      variant: 'camera' as const,
    },
  ];
};

const renderMenuItem = (
  item: HeaderMenuAction,
  onClose: () => void,
  props?: { divider?: boolean },
): JSX.Element => {
  const key = `${item.to}-${item.label}`;

  return (
    <Tooltip key={key} title={item.tooltip}>
      {/* span wrapper lets Tooltip receive pointer events even when MenuItem is disabled */}
      <span>
        <MenuItem
          disabled={item.disabled}
          component={Link}
          to={item.to}
          onClick={onClose}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
          }}
          {...props}
        >
          {item.iconSrc && (
            <img src={item.iconSrc} alt="" width={24} height={24} />
          )}
          {item.label}
        </MenuItem>
      </span>
    </Tooltip>
  );
};

export const HeaderBar = (props: HeaderBarProps): JSX.Element => {
  const {
    title,
    action,
    elevation = 4,
    forceDark = false,
    iconActions = [],
    menuSections,
  } = props;
  const { open, toggleOpen } = useDrawerNavState();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Flatten for keyboard shortcuts
  const allMenuActions = useMemo(
    () => menuSections?.flatMap((s) => s.items) ?? [],
    [menuSections],
  );
  useHeaderActionCommandPaletteActions(allMenuActions);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const showMenuActions = allMenuActions.length > 0;

  const appBar = (
    <HeaderAppBar
      title={title}
      action={action}
      elevation={elevation}
      iconActions={iconActions}
      showMenuActions={showMenuActions}
      toggleOpen={toggleOpen}
      onMenuClick={handleClick}
    />
  );

  return (
    <>
      {forceDark ? <ForcedTheme mode="dark">{appBar}</ForcedTheme> : appBar}

      {showMenuActions && (
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            list: {
              sx: {
                padding: 0,
                minWidth: 175,
              },
            },
          }}
        >
          {menuSections &&
            menuSections.map(({ label, items }, sectionIdx) => [
              label && (
                <ListSubheader
                  key={`section-${label}`}
                  sx={{
                    lineHeight: '32px',
                    borderBottom: 1,
                    borderColor: 'divider',
                    fontWeight: 'bold',
                    fontSize: getDenseResponsiveFontSize('0.875rem'),
                  }}
                >
                  {label}
                </ListSubheader>
              ),
              ...items.map((item, idx) =>
                renderMenuItem(item, handleClose, {
                  divider: !(
                    sectionIdx === menuSections.length - 1 &&
                    idx === items.length - 1
                  ),
                }),
              ),
            ])}
        </Menu>
      )}

      <DrawerNav open={open} toggleOpen={toggleOpen} />
    </>
  );
};
